import { Box, Popover, PopoverActions, Typography } from '@mui/material';
import { getDropdownPaperStyles, V2ThemeProvider } from '@onevision-resources/design-theme';
import { lazy, Suspense, useRef } from 'react';
import useResizeObserver from 'use-resize-observer';

// The library bundles its emoji data, so it loads only once a picker is first opened.
const ReactionPickerContent = lazy(() => import('./reaction-picker-content'));

export interface ReactionPickerProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Called with the chosen emoji's native character; the picker closes afterwards. */
  onSelect: (emoji: string) => void;
  /** A caption under the picker, e.g. that only the team sees reactions. */
  notice?: string;
  /** Compact sizing that matches the portal's body text. */
  dense?: boolean;
}

/**
 * A popover emoji picker for adding a reaction. Escape closes it and returns focus. It stays
 * fully on screen as it loads and expands from the quick set to the full picker.
 */
export const ReactionPicker = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  notice,
  dense = true,
}: ReactionPickerProps) => {
  const actionRef = useRef<PopoverActions>(null);
  // Popover places itself once on open, before the lazy picker renders or expands.
  const { ref: contentRef } = useResizeObserver<HTMLDivElement>({
    onResize: () => actionRef.current?.updatePosition(),
  });

  const handleSelect = (emoji: string) => {
    onSelect(emoji);
    onClose();
  };

  return (
    <V2ThemeProvider>
      <Popover
        open={Boolean(open && anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        action={actionRef}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              ...getDropdownPaperStyles(theme),
              marginTop: theme.spacing(0.5),
            }),
          },
        }}
      >
        <Box ref={contentRef}>
          <Suspense fallback={null}>
            <ReactionPickerContent onSelect={handleSelect} dense={dense} />
          </Suspense>
          {notice ? (
            <Typography
              variant="caption"
              component="p"
              sx={(theme) => ({
                color: theme.palette.custom.OV.sys.onSurfaceVariant,
                paddingX: theme.spacing(1.5),
                paddingBottom: theme.spacing(1),
              })}
            >
              {notice}
            </Typography>
          ) : null}
        </Box>
      </Popover>
    </V2ThemeProvider>
  );
};
